import api from "./api.ts";
import { ACL } from "../pinia/auth.ts";
import type { ParsedUserAgent } from "../utils/userAgent";
import { clearSessionToken, setSessionToken } from "../capacitor/session-token";

// One occupant of the user's session-slot pool, shown on the session-limit
// screen. Mirrors services.SessionSlotConsumer on the backend.
export interface SessionSlotGrant {
  knoc: string;
  ip?: string;
  expiresAt?: string;
}

export interface SessionSlotConsumer {
  kind: "session" | "agent" | "link";
  id: string;
  name?: string;
  ip?: string;
  authMethod?: string;
  userAgent?: string;
  parsedUserAgent?: ParsedUserAgent | null;
  createdAt: string;
  expiresAt?: string;
  // Agentic-access lifecycle state (agent kind only) and when it last changed.
  status?: "pending_link" | "pending_approval" | "approved";
  statusChangedAt?: string;
  grants?: SessionSlotGrant[];
  current: boolean;
}

export interface SessionSlots {
  limit: number;
  used: number;
  hasSlot: boolean;
  consumers: SessionSlotConsumer[];
}

class AuthService {
  getTheme() {
    return api.get("/theme");
  }

  getSystemStatus() {
    return api.get("/system_status");
  }

  submitDiscoveredIps(ips) {
    return api.post("/discovered-ips", { IPs: ips });
  }

  login(credentials) {
    return api.post("/login", credentials).then(async (response) => {
      // On native, the server returns the session token in the response body
      // and skips Set-Cookie. Persist it so the request interceptor can
      // attach it as Authorization: Bearer on subsequent calls. On web
      // setSessionToken is a no-op and the HttpOnly cookie carries the
      // session.
      if (typeof response.data === "string" && response.data) {
        await setSessionToken(response.data);
      }
      return response.data;
    });
  }

  twoFactorVerify(code) {
    return api.post("/otp/verify", code).then((response) => {
      return response.data;
    });
  }

  twoFactorOnboard() {
    return api.get("/otp/generate").then((response) => {
      return response.data;
    });
  }

  adminLogin(credentials) {
    return api.post("/admin/login", credentials).then((response) => {
      return response.data;
    });
  }

  adminTwoFactorVerify(code) {
    return api.post("/admin/otp/verify", code).then((response) => {
      return response.data;
    });
  }

  adminTwoFactorOnboard() {
    return api.get("/admin/otp/generate").then((response) => {
      return response.data;
    });
  }

  logout() {
    return api.post("/logout").then(async (response) => {
      // Always clear the native bearer; on web this is a no-op.
      await clearSessionToken();
      return response.data;
    });
  }

  adminLogout() {
    return api.post("/admin/logout").then((response) => {
      return response.data;
    });
  }

  grantAcl(aclId: string, ips: any, ticketNumber: string, totpCode?: string) {
    let res: {
      ACLID: string;
      IPs: any;
      TicketNumber: string | null;
      TOTPCode: string | null;
    } = {
      ACLID: aclId,
      IPs: ips,
      TicketNumber: null,
      TOTPCode: null,
    };
    if (ticketNumber) {
      res.TicketNumber = ticketNumber;
    }
    if (totpCode) {
      res.TOTPCode = totpCode;
    }

    return api.post("/grant", res).then((response) => {
      return response.data;
    });
  }

  revokeAcl(aclId) {
    return api.delete(`/sessions/current/granted-acls/${aclId}`).then((response) => {
      return response.data;
    });
  }

  status() {
    return api.get("/status").then((response) => {
      const user = response.data;
      if (!user.ACLs) user.ACLs = [];
      user.ACLs.forEach((acl: ACL) => {
        // Inject token into Knocproxy URLs if we've got one
        if (acl.Config?.UriToken) {
          const url = new URL(acl.URL);
          url.searchParams.set("knoctoken", acl.Config.UriToken);
          acl.URL = url.toString();
        }
      });
      return user;
    });
  }

  adminStatus() {
    return api.get("/admin/status").then((response) => {
      return response.data;
    });
  }

  renameServer(id: string, displayName: string) {
    return api.patch(`/admin/servers/${id}`, { DisplayName: displayName }).then((response) => {
      return response.data;
    });
  }

  forgetServer(id: string) {
    return api.delete(`/admin/servers/${id}`).then((response) => {
      return response.data;
    });
  }

  getFleetStatus() {
    // The endpoint sends no cache headers, so a per-call cache-buster forces a
    // fresh fetch rather than a stale browser-cached response.
    return api.get("/admin/fleet/status", { params: { _: Date.now() } }).then((response) => {
      return response.data;
    });
  }

  setFleetTopology(layout: unknown) {
    return api.put("/admin/fleet/topology", layout).then((response) => {
      return response.data;
    });
  }

  setFleetPolicy(settings: {
    StopOutdatedServers: boolean;
    ServerNameHeader: { Enabled: boolean; DurationSeconds: number };
  }) {
    return api.post("/admin/fleet/policy", settings).then((response) => {
      return response.data;
    });
  }

  setServerRotation(id: string, exclude: boolean, durationSeconds = 0) {
    return api
      .post(`/admin/servers/${id}/rotation`, { Exclude: exclude, DurationSeconds: durationSeconds })
      .then((response) => {
        return response.data;
      });
  }

  setServerDrain(id: string, drain: boolean, durationSeconds = 0) {
    return api
      .post(`/admin/servers/${id}/drain`, { Drain: drain, DurationSeconds: durationSeconds })
      .then((response) => {
        return response.data;
      });
  }

  changePassword(data) {
    return api.put("/password", data).then((response) => {
      return response.data;
    });
  }

  revokeDelegatedIPLink(grantId: string, linkId: string) {
    return api.delete(`/grant/${grantId}/companion-ip-link/${linkId}`).then((response) => {
      return response.data;
    });
  }

  listPersistentIPDevices() {
    return api.get("/sessions/current/persistent-ip-devices").then((response) => {
      return response.data;
    });
  }

  createPersistentIPDevice(data: {
    name: string;
    ip: string;
    aclIds: string[];
    ticketNumber?: string;
    totpCode?: string;
  }) {
    return api.post("/sessions/current/persistent-ip-devices", data).then((response) => {
      return response.data;
    });
  }

  updatePersistentIPDevice(
    deviceId: string,
    data: { name: string; ip: string; aclIds: string[]; ticketNumber?: string; totpCode?: string },
  ) {
    return api.put(`/sessions/current/persistent-ip-devices/${deviceId}`, data).then((response) => {
      return response.data;
    });
  }

  deletePersistentIPDevice(deviceId: string) {
    return api.delete(`/sessions/current/persistent-ip-devices/${deviceId}`).then((response) => {
      return response.data;
    });
  }

  setPersistentIPDeviceEnabled(deviceId: string, data: { enabled: boolean; ticketNumber?: string; totpCode?: string }) {
    return api.post(`/sessions/current/persistent-ip-devices/${deviceId}/set-enabled`, data).then((response) => {
      return response.data;
    });
  }

  setAllPersistentIPDevicesEnabled(data: { enabled: boolean; ticketNumber?: string; totpCode?: string }) {
    return api.post("/sessions/current/persistent-ip-devices/set-enabled", data).then((response) => {
      return response.data;
    });
  }

  batchAddPersistentIPs(data: { aclIDs: string[]; persistentIPs: string[]; ticketNumber?: string; totpCode?: string }) {
    return api.post("/companion-ips/batch", data).then((response) => {
      return response.data;
    });
  }

  createAgenticAccess(data: { name: string; durationSeconds: number; aclIDs: string[] }) {
    return api.post("/sessions/current/agentic-accesses", data).then((response) => {
      return response.data;
    });
  }

  listAgenticAccesses() {
    return api.get("/sessions/current/agentic-accesses").then((response) => {
      return response.data;
    });
  }

  // expectedIP echoes the redeemed IP the approver was shown; the server
  // rejects the approval if the agent re-redeemed from a different address.
  approveAgenticAccess(eaId: string, expectedIP: string) {
    return api.post(`/sessions/current/agentic-accesses/${eaId}/approve`, { expectedIP }).then((response) => {
      return response.data;
    });
  }

  revokeAgenticAccess(eaId: string) {
    return api.delete(`/sessions/current/agentic-accesses/${eaId}`).then((response) => {
      return response.data;
    });
  }

  getSessionSlots(): Promise<SessionSlots> {
    return api.get("/sessions/current/session-slots").then((response) => {
      return response.data;
    });
  }

  revokeSessionSlot(kind: string, id: string) {
    return api.delete(`/sessions/current/session-slots/${kind}/${id}`).then((response) => {
      return response.data;
    });
  }

  claimSessionSlot() {
    return api.post("/sessions/current/claim-slot").then((response) => {
      return response.data;
    });
  }

  // Lists the user-login IdPs (primary + additional) whose SAML config built
  // successfully. An empty ID is the primary IdP.
  getLoginIdps() {
    return api.get<{ ID: string; DisplayName: string }[]>("/login/idps").then((response) => response.data);
  }

  useAdminSAML() {
    return api.get("/admin/saml/metadata");
  }
}

export default new AuthService();
